import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import ReactMarkdown from 'react-markdown';
import TypingIndicator from './TypingIndicator';
import MessageBubble from './MessageBubble';
import useChatSocket from './useChatSocket';

import styles from './Chatbot.module.css';
import SendIcon from '@mui/icons-material/Send';
import ReplayIcon from '@mui/icons-material/Replay';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import IconButton from '@mui/material/IconButton';
import PropTypes from 'prop-types';

const markdownComponents = {
    p: ({ children }) => <p style={{ margin: '0.4em 0' }}>{children}</p>,
    strong: ({ children }) => <span style={{ fontWeight: 700 }}>{children}</span>,
    a: ({ href, children }) => <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>,
};

export default function Chatbot({ messages, setMessages, chatReset, onShowFaq }) {

    // To avoid getting stuck in the wrong chat handler in chat.js the socket is
    // torn down and reopened whenever loggedIn changes.
    const loggedIn = useSelector((state) => state.login.value);

    // The WebSocket lifecycle (connect, backoff/reconnect, incoming messages,
    // loading flag, clear-on-reset) lives in this hook so this component can
    // stay focused on rendering + input.
    const { loading, isDisconnected, sendMessage: sendToSocket, retryLastMessage } = useChatSocket({
        setMessages,
        loggedIn,
        chatReset,
    });

    const messagesEndRef = useRef(null);
    const textareaRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const [input, setInput] = useState('');

    const MAX_LENGTH = 200;
    const SHOW_COUNTER_AT = 170;

    const handleChange = (e) => {
        if (e.target.value.length <= MAX_LENGTH) {
            setInput(e.target.value);
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => textareaRef.current?.focus(), 300);
        return () => clearTimeout(timer);
    }, []);

    // Auto-grow the textarea up to a max height as the user types
    useEffect(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
    }, [input]);

    const sendMessage = () => {
        const trimmedInput = input.trim();
        if (!trimmedInput || loading) return;
        // Only show the user's bubble once it's actually on the wire, so a
        // closed socket doesn't leave a message that never gets a reply.
        if (!sendToSocket(trimmedInput)) return;

        const newMessage = { id: Date.now(), sender: 'user', text: trimmedInput };
        setMessages(prev => [...prev, newMessage]);
        setInput('');
    };

    // Handle pressing Enter key (Shift+Enter inserts a newline)
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    return (
        <div className={styles.wrapper}>
            <div className={styles['messages-container']}>
                {messages.map(msg => msg.sender === 'system' ? (
                    <div key={msg.id} className={styles['system-message']}>
                        {msg.text}
                    </div>
                ) : (
                    <MessageBubble key={msg.id} sender={msg.sender}>
                        {msg.sender === 'bot'
                            ? <ReactMarkdown components={markdownComponents}>{msg.text}</ReactMarkdown>
                            : <span className={styles['user-text']}>{msg.text}</span>
                        }
                        {msg.meta && <div className={styles.meta}>{msg.meta}</div>}
                        {msg.retryable && (
                            <button onClick={retryLastMessage} className={styles['retry-button']}>
                                <ReplayIcon sx={{ fontSize: 14, marginRight: '4px' }} />
                                Try again
                            </button>
                        )}
                    </MessageBubble>
                ))}
                {loading && <TypingIndicator />}
                <div ref={messagesEndRef} />
            </div>

            <div className={styles['input-container']}>
                <div className={styles['input-field']}>
                    <textarea
                        ref={textareaRef}
                        rows={1}
                        placeholder={isDisconnected ? "Disconnected" : "Type a message..."}
                        value={input}
                        onChange={handleChange}
                        onKeyDown={handleKeyDown}
                        maxLength={MAX_LENGTH}
                        disabled={isDisconnected}
                        className={styles.input}
                    />
                    {input.length >= SHOW_COUNTER_AT && (
                        <span className={styles['char-counter']}>
                            {input.length} / {MAX_LENGTH}
                        </span>
                    )}
                </div>
                {onShowFaq && (
                    <IconButton
                        onClick={onShowFaq}
                        sx={{ color: '#6b7b8a' }}
                        title="FAQ"
                    >
                        <HelpOutlineIcon />
                    </IconButton>
                )}
                <IconButton
                    onClick={sendMessage}
                    disabled={loading || isDisconnected || input.length > MAX_LENGTH}
                    disableFocusRipple
                    sx={{
                        color: '#6a9fbe',
                        '&.Mui-disabled': { color: '#979393'}
                     }}
                >
                    <SendIcon />
                </IconButton>
            </div>
        </div>
    );
}

Chatbot.propTypes = {
    messages: PropTypes.arrayOf(PropTypes.shape({
        id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
        sender: PropTypes.string.isRequired,
        text: PropTypes.string.isRequired
    })).isRequired,
    setMessages: PropTypes.func.isRequired,
    chatReset: PropTypes.number.isRequired,
    onShowFaq: PropTypes.func
};

